import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

interface HoverTipProps {
  label: ReactNode
  children: ReactElement
  side?: 'top' | 'bottom'
  /** start = align tooltip left edge to trigger left, end = right edge, center (default). */
  align?: 'start' | 'center' | 'end'
  /** Show delay (ms). Default 120. `delay` and `delayDuration` are interchangeable. */
  delay?: number
  delayDuration?: number
  /** Wrap children in an inline-flex span so disabled buttons still surface hover. */
  wrapDisabled?: boolean
  /** Minimum bubble width (px) — avoids shrink-to-fit collapsing multi-line tooltips. */
  bubbleMinWidth?: number
  /** Override default bubble max-width (px). */
  bubbleMaxWidth?: number
  /** Suppress the tooltip: hides a visible bubble and blocks new ones while
      true (e.g. while the trigger is being dragged). */
  disabled?: boolean
}

interface Coords { top: number; left: number; transformX: string }

// Global hover tooltip — non-invasive: clones the child to attach ref + mouse
// handlers, then renders the bubble in a body-portal positioned by
// getBoundingClientRect. Style matches the submit-button tooltip in
// unified-review (dark #1f2937 pill, 12px text, arrow).
//
// cloneElement (vs Radix Slot) avoids the DOM-wrapping that caused queue tab
// counts to disappear when wrapped via Tooltip.Trigger asChild.
export function HoverTip({
  label,
  children,
  side = 'top',
  align = 'center',
  delay,
  delayDuration,
  wrapDisabled = false,
  bubbleMinWidth,
  bubbleMaxWidth,
  disabled = false,
}: HoverTipProps) {
  const effectiveDelay = delayDuration ?? delay ?? 120
  const triggerRef = useRef<HTMLElement | null>(null)
  const bubbleRef = useRef<HTMLDivElement | null>(null)
  const timeoutRef = useRef<number | null>(null)
  const [coords, setCoords] = useState<Coords | null>(null)

  const measure = useCallback((): Coords | null => {
    const el = triggerRef.current
    if (!el) return null
    const r = el.getBoundingClientRect()
    let left: number
    let transformX: string
    if (align === 'start') { left = r.left; transformX = '0' }
    else if (align === 'end') { left = r.right; transformX = '-100%' }
    else { left = r.left + r.width / 2; transformX = '-50%' }
    return {
      top: side === 'top' ? r.top - 8 : r.bottom + 8,
      left,
      transformX,
    }
  }, [side, align])

  const show = useCallback(() => {
    if (disabled) return
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    const run = () => {
      const c = measure()
      if (c) setCoords(c)
    }
    if (effectiveDelay <= 0) run()
    else timeoutRef.current = window.setTimeout(run, effectiveDelay)
  }, [measure, effectiveDelay, disabled])

  const hide = useCallback(() => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    setCoords(null)
  }, [])

  // Going disabled mid-hover (e.g. drag started) must also dismiss an
  // already-visible bubble, not just block future ones.
  useEffect(() => {
    if (disabled) hide()
  }, [disabled, hide])

  useEffect(() => () => { if (timeoutRef.current) window.clearTimeout(timeoutRef.current) }, [])

  // Clamp inside viewport once we know the bubble's measured size.
  useLayoutEffect(() => {
    if (!coords) return
    const bubble = bubbleRef.current
    if (!bubble) return
    const bw = bubble.offsetWidth
    const bh = bubble.offsetHeight
    const PAD = 8
    const vw = window.innerWidth

    // Horizontal clamp
    let visualLeft: number
    if (coords.transformX === '-50%') visualLeft = coords.left - bw / 2
    else if (coords.transformX === '-100%') visualLeft = coords.left - bw
    else visualLeft = coords.left
    let deltaX = 0
    if (visualLeft < PAD) deltaX = PAD - visualLeft
    else if (visualLeft + bw > vw - PAD) deltaX = (vw - PAD) - (visualLeft + bw)

    // Vertical clamp — prevent the bubble from going above the viewport edge.
    // zIndex:9999 already renders above the fixed header so no extra header
    // clearance is needed; PAD just keeps it from clipping off-screen.
    const TOP_BOUNDARY = PAD
    const visualTop = side === 'top' ? coords.top - bh : coords.top
    let deltaY = 0
    if (visualTop < TOP_BOUNDARY) deltaY = TOP_BOUNDARY - visualTop

    if (deltaX !== 0 || deltaY !== 0) {
      setCoords({ ...coords, left: coords.left + deltaX, top: coords.top + deltaY })
    }
  }, [coords, side])

  if (!isValidElement(children) || !label) return children as ReactElement

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const childProps = children.props as any

  // When wrapDisabled, an inline-flex span captures pointer events even though
  // the disabled <button> inside does not fire them.
  if (wrapDisabled) {
    return (
      <>
        <span
          ref={(node) => { triggerRef.current = node }}
          style={{ display: 'inline-flex' }}
          onMouseEnter={show}
          onMouseLeave={hide}
          onFocus={show}
          onBlur={hide}
        >
          {children}
        </span>
        {renderBubble(coords, bubbleRef, label, side, bubbleMinWidth, bubbleMaxWidth)}
      </>
    )
  }

  const cloned = cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const r = (children as any).ref
      if (typeof r === 'function') r(node)
      else if (r && typeof r === 'object') r.current = node
    },
    onMouseEnter: (e: MouseEvent) => { show(); childProps.onMouseEnter?.(e) },
    onMouseLeave: (e: MouseEvent) => { hide(); childProps.onMouseLeave?.(e) },
    onFocus: (e: FocusEvent) => { show(); childProps.onFocus?.(e) },
    onBlur: (e: FocusEvent) => { hide(); childProps.onBlur?.(e) },
  } as Record<string, unknown>)

  return (
    <>
      {cloned}
      {renderBubble(coords, bubbleRef, label, side, bubbleMinWidth, bubbleMaxWidth)}
    </>
  )
}

function renderBubble(
  coords: Coords | null,
  bubbleRef: React.RefObject<HTMLDivElement | null>,
  label: ReactNode,
  side: 'top' | 'bottom',
  bubbleMinWidth?: number,
  bubbleMaxWidth?: number,
) {
  if (!coords) return null
  return createPortal(
    <div
      ref={bubbleRef}
      role="tooltip"
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        transform: `translate(${coords.transformX}, ${side === 'top' ? '-100%' : '0'})`,
        background: '#1f2937',
        color: 'white',
        padding: bubbleMinWidth != null ? '10px 14px' : '6px 10px',
        borderRadius: 'var(--r-sm, 6px)',
        fontSize: 12,
        lineHeight: 1.45,
        minWidth: bubbleMinWidth ?? 180,
        maxWidth: bubbleMaxWidth ?? 340,
        boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
        border: '1px solid rgba(255,255,255,0.08)',
        // Sit above the code-matrix add/edit popover (z=10000). Higher stack
        // layers (Radix Dialog overlays typically) use z-index outside this
        // range, so 10050 stays underneath modals without hiding matrix
        // descriptions behind them.
        zIndex: 10050,
        pointerEvents: 'none',
        whiteSpace: 'normal',
      }}
    >
      {label}
    </div>,
    document.body,
  )
}
